import React from "react";
import * as BadsIcons from "@hm-group/bads-react/icons";

export interface BadsIconsProps {
  /**
   * The icon's name, as the CDN web component took it — kebab or snake case,
   * e.g. "article-hanger" or "quick_quantify". Kept as a string because several
   * call sites pass a value that arrives from the API (`actions.imagePath`,
   * tooltip content), not a literal.
   */
  type: string;
  size?: "xsmall" | "small" | "normal" | "large" | "xlarge";
  color?: string;
  /** Retained for call sites that still pass it; harmless on an SVG. */
  slot?: string;
}

/**
 * The design system's icon, from the npm package.
 *
 * This rendered `<bads-icon-wrapper-v1><bads-icon-v2>` — custom elements that
 * only existed because useBads injected the deprecated CDN loader, so the app
 * pulled BADS in twice. The npm package ships the same icons, including the
 * domain ones this app depends on (ArticleHanger, ColourwayConnection,
 * CarryOver, QuickQuantify, Storefront), which is what made dropping the CDN
 * possible at all.
 *
 * Resolved through the namespace rather than a hand-written map: the names come
 * from data in several places, so a fixed list would silently miss whatever the
 * API sends next. The package exports 149 of them without the "Icon" suffix —
 * "article-hanger" is `ArticleHanger`.
 */
const SIZE_PX: Record<NonNullable<BadsIconsProps["size"]>, string> = {
  xsmall: "12",
  small: "16",
  normal: "24",
  large: "32",
  xlarge: "40",
};

type IconComponent = (props: {
  width?: string;
  height?: string;
  color?: string;
  slot?: string;
}) => React.JSX.Element;

/** "article-hanger" and "quick_quantify" both become "ArticleHanger". */
const toExportName = (type: string): string =>
  type
    .split(/[-_\s.]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

const warned = new Set<string>();

export const resolveIcon = (type: string): IconComponent | null => {
  const name = toExportName(type);
  const icon = (BadsIcons as unknown as Record<string, IconComponent>)[name];
  if (icon) return icon;

  // Names arrive from the API, so an unknown one is a data problem rather than
  // a coding error — warn once and render nothing, instead of throwing inside a
  // grid cell and taking the page down with it.
  if (!warned.has(type)) {
    warned.add(type);
    console.warn(`[BadsIcon] no icon named "${name}" (from "${type}")`);
  }
  return null;
};

const BadsIcon: React.FC<BadsIconsProps> = ({
  type,
  size = "normal",
  color = "black",
  slot,
}) => {
  const icon = resolveIcon(type);
  if (!icon) return null;

  const px = SIZE_PX[size];

  // createElement rather than <Icon />, because the lint rule cannot tell a
  // component looked UP during render from one defined during render. The
  // reference is the package's own export and is stable for a given name, so
  // there is no remount — but the JSX form reads like the hazard the rule
  // exists for, and this says plainly that it is a lookup.
  return React.createElement(icon, {
    width: px,
    height: px,
    // The web component special-cased search to grey; kept so the search field
    // does not change appearance with this swap.
    color: type === "search" ? "#666666" : color,
    slot,
  });
};

export default BadsIcon;
